import { createSlice } from "@reduxjs/toolkit"
import { persistReducer } from "redux-persist"
import storage from "redux-persist/lib/storage"

import { STATUS_META } from "../constants/constants"
import { RECEIPT_BUSINESS_SLICE_NAME as SLICE_NAME } from "./business.constants"
import {
  activate,
  getActivationDescriptors,
  getActivationPrefill,
  getCodes,
  getDescriptors,
  getPrograms,
  getStatusLogs,
  setDescriptorStatus,
  setMatchType
} from "./business.thunks"

// Business tab state (CHA1-1967). Wholly server-owned — every mount refetches,
// nothing here is persisted between sessions. Descriptors are kept per-program
// so switching the sub-tab does not clobber the other program's rows, and
// status logs are keyed by record_id so two popups do not overwrite each other.

const emptyDescriptors = () => ({
  rows: [],
  count: 0,
  page: 1,
  fetching: false,
  error: null
})

const initialState = {
  programs: {
    data: null,
    fetching: false,
    error: null
  },
  descriptors: {
    cc: emptyDescriptors(),
    oi: emptyDescriptors()
  },
  statusLogs: {},
  // Which rows have a write in flight, so each control can show its own spinner
  // (CHA1-2614). Counters, not booleans: both dropdowns stay open for a second
  // change before the first is confirmed, and the earlier response settling must
  // not clear the later one's indicator.
  statusSaving: {},
  matchTypeSaving: {},
  activation: {
    saving: false,
    error: null
  },
  // Unfiltered descriptor list for the activation modal only. Kept separate
  // from `descriptors` so search/status filters on the page don't affect the
  // locked-chips list inside the modal.
  activationDescriptors: {
    rows: [],
    // Which program `rows` describes. The modal is not remounted when the
    // program changes, so rows alone cannot say whether they still apply --
    // and a descriptor of the program just left is not a locked chip here,
    // it is a new one this form would submit.
    program: null,
    fetching: false,
    error: null
  },
  // §8.0 Codes the activation modal opens with. `loaded` exists because an
  // account with no codes yet is a legitimate all-empty answer — without it the
  // modal cannot tell "nothing saved" from "not fetched yet" and would keep
  // re-seeding empty fields over what the merchant is typing.
  activationPrefill: {
    arnCodes: [],
    binCodes: [],
    caidCodes: [],
    displayMode: "ARN",
    // The merchant's own BIN/CAID opt-in, as the account stores it — the same
    // flag the alerts form keeps in `use_bin_caid_codes`. `null` means nobody
    // has chosen yet, which is not the same as having chosen ARN: only then may
    // the modal fall back to inferring the mode from `displayMode`.
    useBinCaid: null,
    loaded: false,
    fetching: false,
    error: null
  },
  // Support's Order Insight codes views (BLOCK 5). `availableCodeTypes` is the
  // cycle the toggle link walks — server-ordered and listing only what this BA
  // has, so a BA with one code type gets a 2-state cycle for free. Which view is
  // open is component state, not stored here: the page must always open on the
  // descriptor list.
  codes: {
    availableCodeTypes: [],
    arn: [],
    bin: [],
    caid: [],
    fetching: false,
    error: null
  }
}

const beginSaving = (map, recordId) => {
  if (recordId) map[recordId] = (map[recordId] || 0) + 1
}

const endSaving = (map, recordId) => {
  if (!recordId || !map[recordId]) return
  if (map[recordId] > 1) map[recordId] -= 1
  else delete map[recordId]
}

const slice = createSlice({
  name: SLICE_NAME,
  initialState,
  reducers: {
    setProp(state, { payload: { key, value } }) {
      state[key] = value
    },
    resetBusiness() {
      return initialState
    },
    clearActivationError(state) {
      state.activation.error = null
    }
  },
  extraReducers: (builder) => {
    /* --- getPrograms --- */
    builder.addCase(getPrograms.pending, (state) => {
      state.programs.fetching = true
      state.programs.error = null
    })
    builder.addCase(getPrograms.fulfilled, (state, { payload }) => {
      state.programs.fetching = false
      state.programs.data = payload?.programs || []
    })
    builder.addCase(getPrograms.rejected, (state, { payload }) => {
      state.programs.fetching = false
      state.programs.error = payload || "Failed to load programs"
    })

    /* --- getDescriptors --- */
    builder.addCase(getDescriptors.pending, (state, { meta }) => {
      const key = meta.arg?.program
      if (!key || !state.descriptors[key]) return
      state.descriptors[key].fetching = true
      state.descriptors[key].error = null
    })
    builder.addCase(getDescriptors.fulfilled, (state, { payload }) => {
      const { program, page, data } = payload
      if (!state.descriptors[program]) return
      state.descriptors[program].fetching = false
      state.descriptors[program].count = data?.count ?? 0
      state.descriptors[program].page = page
      // Page 1 replaces; later pages append (infinite scroll per §6.2 + spec
      // BLOCK 3.3 "20 per batch, infinite scroll").
      state.descriptors[program].rows =
        page > 1
          ? [...state.descriptors[program].rows, ...(data?.results || [])]
          : data?.results || []
    })
    builder.addCase(getDescriptors.rejected, (state, { meta, payload }) => {
      const key = meta.arg?.program
      if (!key || !state.descriptors[key]) return
      state.descriptors[key].fetching = false
      state.descriptors[key].error = payload || "Failed to load descriptors"
    })

    /* --- getActivationDescriptors --- */
    builder.addCase(getActivationDescriptors.pending, (state) => {
      state.activationDescriptors.fetching = true
      state.activationDescriptors.error = null
      state.activationDescriptors.rows = []
      state.activationDescriptors.program = null
    })
    builder.addCase(getActivationDescriptors.fulfilled, (state, { meta, payload }) => {
      state.activationDescriptors.fetching = false
      state.activationDescriptors.rows = payload?.results || []
      state.activationDescriptors.program = meta.arg?.program || null
    })
    builder.addCase(getActivationDescriptors.rejected, (state, { payload }) => {
      state.activationDescriptors.fetching = false
      state.activationDescriptors.error = payload || "Failed to load descriptors"
    })

    /* --- getActivationPrefill --- */
    builder.addCase(getActivationPrefill.pending, (state) => {
      state.activationPrefill.fetching = true
      state.activationPrefill.error = null
      // Cleared so a reopened modal seeds from this fetch, not the last one.
      // A submit changes the codes, so the values held here are stale the
      // moment the modal closes.
      state.activationPrefill.loaded = false
    })
    builder.addCase(getActivationPrefill.fulfilled, (state, { payload }) => {
      state.activationPrefill.fetching = false
      state.activationPrefill.loaded = true
      state.activationPrefill.arnCodes = payload?.arn_codes || []
      state.activationPrefill.binCodes = payload?.bin_codes || []
      state.activationPrefill.caidCodes = payload?.caid_codes || []
      state.activationPrefill.displayMode = payload?.display_mode || "ARN"
      // `??` and not `||`: a stored `false` is a real answer (the merchant
      // submitted ARN), and collapsing it to `null` would send the modal back
      // to guessing from the codes the account happens to hold.
      state.activationPrefill.useBinCaid = payload?.use_bin_caid_codes ?? null
    })
    builder.addCase(getActivationPrefill.rejected, (state, { payload }) => {
      state.activationPrefill.fetching = false
      state.activationPrefill.error = payload || "Failed to load codes"
    })

    /* --- activate --- */
    builder.addCase(activate.pending, (state) => {
      state.activation.saving = true
      state.activation.error = null
    })
    builder.addCase(activate.fulfilled, (state) => {
      state.activation.saving = false
    })
    builder.addCase(activate.rejected, (state, { payload }) => {
      state.activation.saving = false
      state.activation.error = payload || "Activation failed"
    })

    /* --- getStatusLogs --- */
    builder.addCase(getStatusLogs.pending, (state, { meta }) => {
      const id = meta.arg?.recordId
      if (!id) return
      state.statusLogs[id] = { ...(state.statusLogs[id] || {}), fetching: true, error: null }
    })
    builder.addCase(getStatusLogs.fulfilled, (state, { payload }) => {
      state.statusLogs[payload.recordId] = { fetching: false, error: null, data: payload.data }
    })
    builder.addCase(getStatusLogs.rejected, (state, { payload }) => {
      const id = payload?.recordId
      if (!id) return
      state.statusLogs[id] = {
        ...(state.statusLogs[id] || {}),
        fetching: false,
        error: payload?.message || "Failed to load status logs"
      }
    })

    /* --- setMatchType (BLOCK 3.5) --- */
    // Applied to both programs' lists: match type belongs to the descriptor, so
    // a descriptor enrolled in CC and OI must not end up showing two values.
    builder.addCase(setMatchType.pending, (state, { meta }) => {
      beginSaving(state.matchTypeSaving, meta.arg?.recordId)
    })
    builder.addCase(setMatchType.rejected, (state, { meta }) => {
      endSaving(state.matchTypeSaving, meta.arg?.recordId)
    })
    builder.addCase(setMatchType.fulfilled, (state, { meta, payload }) => {
      endSaving(state.matchTypeSaving, meta.arg?.recordId)
      const descriptorId = payload?.descriptor_id
      if (!descriptorId) return
      for (const program of Object.keys(state.descriptors)) {
        for (const row of state.descriptors[program].rows) {
          if (row.billing_descriptor_id === descriptorId) row.match_type = payload.match_type
        }
      }
    })

    /* --- setDescriptorStatus (BLOCK 3.4) --- */
    // The row keeps whatever status the server settled on, which is not always
    // the one support picked (CHA1-1374 may move it on). `status_group` is not
    // in the response, so it is derived from the same table the list renders by.
    builder.addCase(setDescriptorStatus.pending, (state, { meta }) => {
      beginSaving(state.statusSaving, meta.arg?.recordId)
    })
    builder.addCase(setDescriptorStatus.rejected, (state, { meta }) => {
      endSaving(state.statusSaving, meta.arg?.recordId)
    })
    // Fulfilled only settles once the refetch it triggered has landed (see the
    // thunk), so the spinner stops on the row the new match type is on.
    builder.addCase(setDescriptorStatus.fulfilled, (state, { meta, payload }) => {
      endSaving(state.statusSaving, meta.arg?.recordId)
      const status = payload?.record?.status
      if (!payload?.recordId || !status) return
      for (const program of Object.keys(state.descriptors)) {
        const row = state.descriptors[program].rows.find((r) => r.record_id === payload.recordId)
        if (!row) continue
        row.current_status = status
        row.status_group = STATUS_META[status]?.group || row.status_group
      }
      // The change is logged server-side; drop the cached history so reopening
      // the popup shows the new entry instead of the pre-change list.
      delete state.statusLogs[payload.recordId]
    })

    /* --- getCodes (BLOCK 5) --- */
    builder.addCase(getCodes.pending, (state) => {
      state.codes.fetching = true
      state.codes.error = null
    })
    builder.addCase(getCodes.fulfilled, (state, { payload }) => {
      state.codes.fetching = false
      state.codes.availableCodeTypes = payload?.available_code_types || []
      // Only the lists the requested code_type covers come back populated, so
      // each is replaced rather than merged — a search that matches nothing must
      // empty the view, not leave the previous results on screen.
      state.codes.arn = payload?.arn_codes || []
      state.codes.bin = payload?.bin_codes || []
      state.codes.caid = payload?.caid_codes || []
    })
    builder.addCase(getCodes.rejected, (state, { payload }) => {
      state.codes.fetching = false
      state.codes.error = payload || "Failed to load codes"
    })
  }
})

export const { resetBusiness, clearActivationError, setProp } = slice.actions

/* ------------- Selectors ------------- */

export const selectPrograms = (state) => state.receiptBusiness.programs.data
export const selectProgramsError = (state) => state.receiptBusiness.programs.error

export const selectDescriptors = (program) => (state) =>
  state.receiptBusiness.descriptors[program] || {
    rows: [],
    count: 0,
    page: 1,
    fetching: false,
    error: null
  }

export const selectActivation = (state) => state.receiptBusiness.activation

export const selectActivationDescriptors = (state) => state.receiptBusiness.activationDescriptors
export const selectActivationPrefill = (state) => state.receiptBusiness.activationPrefill

export const selectCodes = (state) => state.receiptBusiness.codes

export const selectStatusLogs = (recordId) => (state) =>
  state.receiptBusiness.statusLogs[recordId] || { data: null, fetching: false, error: null }

// Booleans, not the maps themselves — a subscriber per row would re-render on
// every unrelated write if it compared object identity.
export const selectStatusSaving = (recordId) => (state) =>
  !!state.receiptBusiness.statusSaving[recordId]

export const selectMatchTypeSaving = (recordId) => (state) =>
  !!state.receiptBusiness.matchTypeSaving[recordId]

/* ------------- Persistence ------------- */
// Server-owned; every mount refetches. Persisting would show another BA's data
// before the authoritative fetch resolves.

const PERSISTED_REDUCER_VERSION = 1

export const persistedReducer = persistReducer(
  {
    key: "receiptBusinessThunk",
    storage,
    version: PERSISTED_REDUCER_VERSION,
    blacklist: [
      "programs",
      "descriptors",
      "statusLogs",
      "statusSaving",
      "matchTypeSaving",
      "activation",
      "activationDescriptors"
    ],
    migrate: async (state) => state
  },
  slice.reducer
)

export const reducer = persistedReducer
